import { DialogContent, DialogOverlay } from '@reach/dialog';
import '@reach/dialog/styles.css';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { animated, config, useSpring } from 'react-spring';

import { CloseButton } from 'Buttons';

import ModalContext from './ModalContext';
import './styles.css';

const sizesWidth = {
  large: '49rem',
  medium: '36rem',
  small: '25rem',
};

const onDismissDefault = () => {};

const AnimatedOverlay = animated(DialogOverlay);
const AnimatedContent = animated(DialogContent);

const Modal = ({ ariaLabel, children, size, ...rest }) => {
  const { isOpen, onDismiss } = rest;
  const [isScrollableHeight, setIsScrollableHeight] = useState(false);

  const opacityProps = useSpring({
    config: { duration: 500 },
    opacity: isOpen ? 1 : 0,
  });
  const transformProps = useSpring({
    config: config.molasses,
    transform: isOpen
      ? 'translateY(calc(-50% - 0rem))'
      : 'translateY(calc(-50% - 1rem))',
  });

  return (
    <AnimatedOverlay style={opacityProps} {...rest}>
      <ModalContext.Provider
        value={useMemo(() => {
          return {
            isScrollableHeight,
            setIsScrollableHeight,
          };
        }, [isScrollableHeight, setIsScrollableHeight])}
      >
        <AnimatedContent
          aria-label={ariaLabel}
          style={{
            width: sizesWidth[size],
            ...opacityProps,
            ...transformProps,
          }}
        >
          {onDismiss !== onDismissDefault && (
            <CloseButton
              callback={onDismiss}
              className="absolute top-2 right-2"
            />
          )}
          {children}
        </AnimatedContent>
      </ModalContext.Provider>
    </AnimatedOverlay>
  );
};

Modal.defaultProps = {
  isOpen: false,
  onDismiss: onDismissDefault,
  size: 'medium',
};

Modal.propTypes = {
  /**
   * ARIA label that describes the purpose or content of the modal
   */
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  /**
   * Is the modal open or closed?
   */
  isOpen: PropTypes.bool,
  /**
   * Function to execute when dismissing the modal. Omit to remove the close button and click outside to dismiss functionality.
   */
  onDismiss: PropTypes.func,
  /**
   * Size of the modal container
   */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

export default Modal;
